import { z } from 'zod';

// Per-reviewer outcome within a single council run. Mirrors the orchestrator's
// (diff-vader/plugins/diff-vader/skills/dark-council/scripts/run-council.py)
// reviewer_status shape so we can persist + render it without re-shaping at
// the death-star edge.
//
// `timeout` and `error` were added 2026-05-13 after rewindio/external-user-service#30
// dropped its entire review when supportability hit the per-reviewer 180s
// timeout: the runner emits status='timeout', the schema rejected it, the whole
// artifact upload 400'd, and the PR ended up with no DV review. Schema-level
// acceptance is the first half of the fix; consumers (calibration rollup,
// portal) treat the new statuses as "didn't run successfully" — neither counts
// as a `runs` increment.
//
// `failed` was added 2026-05-14 (split from `skipped` upstream by diff-vader
// PR #52) — denotes a reviewer subprocess that crashed before producing
// output (exit non-zero with no JSON), distinct from `error` (which carries
// the connotation of an LLM/API-side failure) and `timeout` (which exceeded
// the per-reviewer wall clock). The schema-edge gap caused every artifact
// upload to 400 once any reviewer crashed — same failure mode as the
// rewindio/external-user-service#30 incident, just with a fifth literal.
export const CouncilReviewerStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok') }),
  z.object({ status: z.literal('skipped'), reason: z.string() }),
  z.object({ status: z.literal('timeout'), reason: z.string() }),
  z.object({ status: z.literal('error'), reason: z.string() }),
  z.object({ status: z.literal('failed'), reason: z.string() })
]);
export type CouncilReviewerStatus = z.infer<typeof CouncilReviewerStatusSchema>;

// Per-finding record from the council. Mirrors the orchestrator's per-finding
// shape (diff-vader/packages/types/src/council.ts CouncilFindingSchema —
// that file is the source of truth; this one intentionally lags). If
// upstream adds a required field, this schema must be updated to match
// or ingest will reject artifacts. signal_id and signal_name are optional —
// only reviewers with numbered taxonomies emit them. signal_name carries the
// human-readable label (e.g. "Silent exception swallow") so the portal can
// render it without maintaining a parallel lookup table; the SKILL.md remains
// the single source of truth for what each ID means.
export const CouncilFindingSchema = z.object({
  reviewer: z.string(),
  file: z.string(),
  line: z.number().int().nonnegative(),
  message: z.string(),
  confidence: z.number().int().min(0).max(100),
  substantive: z.boolean(),
  signal_id: z.number().int().min(1).nullable().optional(),
  signal_name: z.string().nullable().optional(),
});
export type CouncilFinding = z.infer<typeof CouncilFindingSchema>;

// CI artifact payload from the diff-vader runner
export const CIArtifactSchema = z.object({
  args: z.object({
    owner: z.string(),
    repo: z.string(),
    pull_number: z.number().int(),
    mode: z.enum(['shadow', 'enabled'])
  }),
  input: z.object({
    policy_tier: z.enum(['LOW', 'MEDIUM', 'HIGH']),
    changed_files: z.array(z.string()),
    size_metrics: z.object({
      loc_changed: z.number().int().nonnegative(),
      files_changed: z.number().int().nonnegative()
    }),
    pr_author_is_human: z.boolean()
  }).passthrough(),
  verdict: z.object({
    state: z.enum(['APPROVE', 'NEEDS_HUMAN', 'REQUEST_CHANGES', 'TOO_LARGE', 'DEFERRED']),
    reason: z.string(),
    side_effects: z.record(z.string(), z.unknown())
  }),
  // Full findings array from the council. Optional for back-compat with
  // pre-Plan-2.6 runners that didn't include it. Empty array means the
  // council ran but emitted no findings (a valid output, e.g. PASS verdict
  // with nothing flagged). Absence (undefined) means the runner predates
  // this field — older artifacts in S3 may parse with findings undefined.
  findings: z.array(CouncilFindingSchema).optional(),
  cost: z
    .object({
      tokens_in: z.number().int().nonnegative(),
      tokens_out: z.number().int().nonnegative(),
      usd: z.number().nonnegative(),
      // Cache-aware breakdown of the summed tokens_in (input + cache reads +
      // cache writes). Present only on artifacts from the SDK dispatch path
      // (DV_DISPATCH=sdk); the CLI path emits only the summed tokens_in, so
      // these are optional and absent on historical / CLI-path rows.
      input_tokens: z.number().int().nonnegative().optional(),
      cache_read_input_tokens: z.number().int().nonnegative().optional(),
      cache_creation_input_tokens: z.number().int().nonnegative().optional(),
      per_reviewer: z.record(z.string(), z.object({
        tokens_in: z.number().int().nonnegative(),
        tokens_out: z.number().int().nonnegative(),
        usd: z.number().nonnegative(),
        input_tokens: z.number().int().nonnegative().optional(),
        cache_read_input_tokens: z.number().int().nonnegative().optional(),
        cache_creation_input_tokens: z.number().int().nonnegative().optional()
      }))
    })
    .optional(),
  model_versions: z.record(z.string(), z.string()).optional(),
  // `meta.reviewer_status`: per-reviewer ran/skipped outcome. Optional for
  // back-compat with runner versions that pre-date Plan 2.4b. Passthrough
  // record-of-unknown for any other meta keys we haven't typed yet. `.nullish`
  // (not `.optional`) tolerates a runner that emits `"meta": null` rather
  // than omitting the key — without this, a 400 ingest failure would be the
  // first signal of that drift.
  // A3b: policy resolution outcome for the org+repo on this PR. Optional
  // for back-compat with pre-A3b runners. death-star ingests this onto the
  // OPTIN row (org axis) and the REPO CONFIG row (repo axis) so the config
  // UI can render the customized / inherited / synthesized badge and the
  // "Show orgs needing policy audit" filter.
  policy_source: z
    .object({
      org: z.enum(['central', 'synthesized']),
      repo: z.enum(['central', 'inherited', 'synthesized']),
    })
    .optional(),
  meta: z
    .object({
      reviewer_status: z.record(z.string(), CouncilReviewerStatusSchema).optional(),
      // Wall-clock milliseconds of the council orchestration window measured
      // by the runner (around invokeCouncil, exclusive of workflow setup).
      // Optional for back-compat with pre-duration runner builds.
      // `.int()` rejects Infinity, NaN, and fractional ms — the runner rounds
      // before sending, but the schema is the boundary that protects DDB
      // from a regressed/mocked sender.
      duration_ms: z.number().int().nonnegative().optional(),
      // PR context — populated by the diff-vader runner from octokit.pulls.get.
      // Optional for back-compat with pre-PR-2 emitters; renders as `—` in the
      // portal until populated.
      title: z.string().optional(),
      author: z.string().optional(),
      branch: z.string().optional(),
      // Per-finding review-comment IDs + body excerpts for the polling
      // reaction-ingest Lambda. Each entry's body_excerpt is up to 500 chars
      // (post-marker), which the polling Lambda persists onto reaction rows
      // for the clusterer's keyword extraction. Reviewer is captured here so
      // we don't have to re-parse the marker on the death-star side.
      review_comments: z
        .array(z.object({
          id: z.number().int(),
          reviewer: z.string(),
          body_excerpt: z.string(),
          // Populated by poll-reactions after it queries GitHub for the
          // comment's anchor: true = the line was modified before merge
          // (acted on), false = anchor still intact (merged through), null =
          // indeterminate (off-diff at posting, or the comment 404'd).
          acted_on: z.boolean().nullable().optional(),
          acted_on_checked_at: z.string().optional(),
          // Populated by ingest-ci-artifact (Phase 2.4b) when a subsequent
          // re-review artifact's verifier_results.by_finding_id flips this
          // comment_id to 'resolved'. Latched true; never written false.
          // Calibration rollup OR's this with acted_on. null/undefined =
          // no verifier signal yet (excluded from denominator alone; only
          // contributes when paired with acted_on).
          acted_on_verifier: z.boolean().nullable().optional(),
          acted_on_verifier_checked_at: z.string().optional()
        }))
        .optional(),
      // review_kind: distinguishes initial PR review from re-review on same PR.
      // Drives portal badge + Phase B/C shadow-window metric splits. Optional for
      // back-compat with pre-verifier-mode artifacts.
      review_kind: z.enum(['initial', 're-review']).optional(),
      // trigger: user-initiated (today's only path, slash-command) vs auto-triggered
      // (reserved for a future push-on-PR auto-trigger). Optional for back-compat.
      trigger: z.enum(['user-initiated', 'auto-triggered']).optional(),
      // Cost-optimization telemetry (Plan 2026-05-28, F1/F3). All optional —
      // each is emitted by the runner only on the specific path that produces
      // it, so presence is the signal. Declared explicitly (rather than left
      // on the meta object's .passthrough()) so ingest-ci-artifact can persist
      // them to DDB for the cost dashboard's F3-short-circuit and F1-adoption
      // charts. Before this, 0/663 artifacts in a 30d window had any of these
      // fields in DDB despite the runner sending short_circuit_reason on the
      // wire.
      //   short_circuit_reason: which gate skipped the council. F3's value is
      //     'predetermined_needs_human_by_size'; other values come from the
      //     pre-existing size_too_large / hard_rules_always_human /
      //     external_contributor short-circuits.
      //   verifier_mode_no_prior_findings_fallthrough: the calibration-miss-
      //     freeze fallthrough fired (verifier mode → fresh council).
      //   delta_payload_used: F1 actually sent a delta diff on the fallthrough
      //     path (true) vs fell back to full diff (false). Absent off the
      //     fallthrough path entirely.
      //   short_circuit_bypassed: a user slash-command suppressed the
      //     hard_rules_always_human short-circuit (DV_USER_OVERRIDE).
      short_circuit_reason: z.string().optional(),
      verifier_mode_no_prior_findings_fallthrough: z.boolean().optional(),
      delta_payload_used: z.boolean().optional(),
      short_circuit_bypassed: z.boolean().optional(),
      // verifier_results: per-finding classification summary emitted when the
      // orchestrator ran in verifier mode. Drives the pr-detail breakdown row
      // and the daily-monitoring resolved/unresolved-rate metrics. Absent for
      // initial reviews and for `--fresh` full re-reviews.
      verifier_results: z
        .object({
          total_prior_findings: z.number().int().nonnegative(),
          resolved: z.number().int().nonnegative(),
          unresolved: z.number().int().nonnegative(),
          partial: z.number().int().nonnegative(),
          withdrawn: z.number().int().nonnegative(),
          by_finding_id: z.record(
            z.string(),
            z.object({
              status: z.enum(['resolved', 'unresolved', 'partial', 'withdrawn']),
              rationale: z.string(),
            })
          ),
        })
        .optional(),
      // `meta.findings_anchor_status`: per-finding anchor outcome (anchored to a `+`
      // line vs dropped because the claimed line wasn't in the diff). Optional for
      // back-compat with pre-Phase-0 runners that didn't compute it. Counters are
      // non-negative ints; `by_reviewer` is sparse — only reviewers that emitted at
      // least one finding have an entry. See diff-vader#79.
      findings_anchor_status: z
        .object({
          total: z.number().int().nonnegative(),
          anchored: z.number().int().nonnegative(),
          dropped_unanchored: z.number().int().nonnegative(),
          by_reviewer: z.record(
            z.string(),
            z.object({
              total: z.number().int().nonnegative(),
              anchored: z.number().int().nonnegative(),
              dropped_unanchored: z.number().int().nonnegative(),
            })
          ),
          // Per-finding diagnostic data for hypothesis A/B classification of
          // anchor failures. Optional for back-compat with pre-#79-followup
          // runners. Modeled as a discriminated union on `drop_reason` so the
          // (reason, claimed_line) invariants are enforced at the schema
          // boundary instead of relying on runner-side discipline:
          //   'malformed_line'   — runner-side `isAnchorable` filter caught a
          //                        genuinely bad shape (negative/non-int line or
          //                        empty file). claimed_line MUST be null. Not
          //                        emitted to GH.
          //   'file_level'       — the reviewer flagged a whole-file concern with
          //                        `line: 0` (a value the council schema legally
          //                        permits, minimum: 0). Not anchorable to a diff
          //                        position, so the runner records it here rather
          //                        than as an inline-anchored finding. claimed_line
          //                        MUST be null. Distinct from malformed_line so
          //                        this telemetry doesn't read expected file-level
          //                        findings as a parse regression. (How the runner
          //                        then renders it on the PR is its concern, not
          //                        this receiver's.) See diff-vader#79 followup.
          //   'not_actionable'   — runner-side `isActionable` filter caught it
          //                        (advisory "no concerns" pattern match). The
          //                        finding was anchorable but suppressed before
          //                        anchor consideration. claimed_line is the
          //                        valid positive int the reviewer emitted.
          //   'outside_diff'     — anchorable + actionable, but the line is not
          //                        in the PR's diff (reviewer reasoned over
          //                        unchanged code). Caught before posting
          //                        (diff-vader#79 Phase 1) so it never rejects
          //                        the batch; the runner body-lists it instead.
          //                        claimed_line is the positive int the
          //                        reviewer emitted.
          //   'gh_rejected_batch'— inline createReview failed because at least
          //                        one comment in the batch had a line outside
          //                        the diff context. All findings in the
          //                        rejected batch are recorded even though
          //                        only some were the cause. claimed_line is
          //                        the positive int the reviewer emitted.
          // See diff-vader#79.
          dropped_details: z
            .array(
              z.discriminatedUnion('drop_reason', [
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.null(),
                  drop_reason: z.literal('malformed_line'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.null(),
                  drop_reason: z.literal('file_level'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('not_actionable'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('outside_diff'),
                }),
                z.object({
                  reviewer: z.string(),
                  file: z.string(),
                  claimed_line: z.number().int().positive(),
                  drop_reason: z.literal('gh_rejected_batch'),
                }),
              ])
            )
            .optional(),
        })
        .optional()
    })
    .passthrough()
    .nullish(),
  ts: z.string()
});
export type CIArtifact = z.infer<typeof CIArtifactSchema>;

// SQS message enqueued onto ReDispatchQueue when a DEFERRED artifact is
// ingested (transient upstream outage). ingest-ci-artifact enqueues attempt 1
// with DelaySeconds=120; ReDispatchWorkerFn self-re-enqueues attempts 2 and 3
// (backoff 300 / 900s) and stops at the cap. See design section I.
//
// head_sha is nullable: present when the runner captured meta.head_sha
// (Class A — Anthropic overload), null when it couldn't (Class B — GitHub
// fetch failed). The worker resolves a null head_sha via octokit and falls
// back to a pull_number-only ledger key if GitHub is also unreachable.
export const ReDispatchJobSchema = z.object({
  owner: z.string().min(1),
  repo: z.string().min(1),
  pull_number: z.number().int().positive(),
  head_sha: z.string().min(1).nullable(),
  // Verdict mode carried from the deferred artifact's args.mode. The worker
  // re-checks getEffectiveDvConfig and dispatches with the fresh config mode;
  // this value is a fallback + observability aid.
  mode: z.enum(['shadow', 'enabled']),
  // 1-based re-dispatch attempt this message represents. Ingest always enqueues
  // attempt 1; the worker enqueues 2 and 3. Cap = 3 re-dispatches (attempt 0 is
  // the original deferred run), 4 total runs.
  attempt: z.number().int().min(1).max(3),
  // Upstream failure classification from meta.failure_class, carried through so
  // the exhaustion Slack page can name the outage. Kept as a free-form string
  // for forward-compat with future classes. Non-empty: the ops page interpolates
  // this directly into the Slack message text, so an empty/whitespace value
  // would render as a blank outage name.
  failure_class: z.string().min(1),
});
export type ReDispatchJob = z.infer<typeof ReDispatchJobSchema>;

export const PagerDutyIncidentSchema = z.object({
  event: z.object({
    event_type: z.string(),
    occurred_at: z.string(),
    data: z.object({
      id: z.string(),
      summary: z.string(),
      description: z.string().optional(),
      html_url: z.string()
    })
  })
});
export type PagerDutyIncident = z.infer<typeof PagerDutyIncidentSchema>;

export const ConfluencePageSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.object({ storage: z.object({ value: z.string() }) }),
  version: z.object({ when: z.string() }),
  _links: z.object({ webui: z.string() }).optional()
});
export type ConfluencePage = z.infer<typeof ConfluencePageSchema>;

// Incident-linkage schemas. The IncidentRecordSchema is the canonical row
// shape stored under an INCIDENT# pk. Links are stored under PR# pks with
// k.incidentLink SKs (see keys.ts). TopologySnapshot is the singleton
// rewind-topology view used by the linker for service → arch_group resolution.
export const IncidentRecordSchema = z.object({
  pk: z.string(),
  sk: z.literal('META'),
  source: z.enum(['confluence-postmortem', 'pagerduty-statuspage']),
  source_url: z.string().url(),
  title: z.string(),
  arch_group: z.enum(['edge', 'engine', 'hero']).nullable(),
  impacted_business_services: z.array(z.string()).default([]),
  occurred_at: z.string().datetime(),
  resolved_at: z.string().datetime().nullable(),
  severity: z.string().nullable(),
  referenced_pd_alarm_url: z.string().url().nullable(),
  ingested_at: z.string().datetime()
});
export type IncidentRecord = z.infer<typeof IncidentRecordSchema>;

// Reverse-index row under a PR# pk listing every incident touching that PR.
// org/repo/pr_number are encoded in pk via k.pr(); incident_id encodes which
// incident this link points to and de-dupes the row from sibling links.
export const IncidentLinkSchema = z.object({
  pk: z.string(),                                  // k.pr(org, repo, pr_number)
  sk: z.string(),                                  // k.incidentLink(incident_id)
  incident_id: z.string(),                         // e.g. 'pm:4483678214', 'sp:PWYRQT0'
  confidence: z.number().int().min(0).max(100),
  reason: z.enum([
    'explicit_pr_url',
    'explicit_pr_shorthand',
    'business_service_match_via_status_page',
    'service_match_via_pm_component',
    'arch_group_only',
    'timing_only'
  ]),
  linked_at: z.string().datetime()
});
export type IncidentLink = z.infer<typeof IncidentLinkSchema>;

export const TopologySnapshotSchema = z.object({
  pk: z.literal('TOPOLOGY#current'),
  sk: z.literal('SNAPSHOT'),
  services: z.record(z.string(), z.object({ repo: z.string(), arch: z.enum(['edge', 'engine', 'hero']) })),
  arch_groups: z.object({
    edge: z.array(z.string()),
    engine: z.array(z.string()),
    hero: z.array(z.string())
  }),
  synced_at: z.string().datetime()
});
export type TopologySnapshot = z.infer<typeof TopologySnapshotSchema>;

export const DvMode = z.enum(['disabled', 'shadow', 'enabled']);
export type DvMode = z.infer<typeof DvMode>;

export const DiffVaderConfigSchema = z.object({
  mode: DvMode,
  rerunOnPush: z.boolean(),
});
export type DiffVaderConfig = z.infer<typeof DiffVaderConfigSchema>;

const ConfigChangeEvent = z.object({
  event_type: z.literal('dv_config_change'),
  scope: z.enum(['org_default', 'repo_override']),
  org: z.string(),
  repo: z.string().optional(),
  before: DiffVaderConfigSchema.nullable(),
  after: DiffVaderConfigSchema.nullable(),
}).superRefine((data, ctx) => {
  if (data.scope === 'repo_override' && !data.repo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'repo is required when scope is "repo_override"',
      path: ['repo'],
    });
  }
  if (data.scope === 'org_default' && data.repo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'repo must be omitted when scope is "org_default"',
      path: ['repo'],
    });
  }
});

const OrgOptedInEvent = z.object({
  event_type: z.literal('org_opted_in'),
  org: z.string(),
});

const OrgOptedOutEvent = z.object({
  event_type: z.literal('org_opted_out'),
  org: z.string(),
  cascade_deleted_count: z.number().int().nonnegative(),
});

const RedundantCleanedEvent = z.object({
  event_type: z.literal('redundant_overrides_cleaned'),
  org: z.string(),
  removed_count: z.number().int().nonnegative(),
  removed_repos: z.array(z.string()),
});

const RbacGrantEvent = z.object({
  event_type: z.literal('rbac_grant'),
  target_email: z.string(),
  old_role: z.union([z.literal('viewer'), z.literal('triager'), z.literal('superAdmin')]).nullable(),
  new_role: z.union([z.literal('triager'), z.literal('superAdmin')]),
});

const RbacRevokeEvent = z.object({
  event_type: z.literal('rbac_revoke'),
  target_email: z.string(),
  old_role: z.union([z.literal('triager'), z.literal('superAdmin')]),
});

const UserLoginEvent = z.object({
  event_type: z.literal('user_login'),
  target_email: z.string(),
});

export const AuditEventSchema = z.union([
  ConfigChangeEvent,
  OrgOptedInEvent,
  OrgOptedOutEvent,
  RedundantCleanedEvent,
  RbacGrantEvent,
  RbacRevokeEvent,
  UserLoginEvent,
]);
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const PolicyVersionSchema = z.object({
  file_path: z.string(),
  commit_sha: z.string(),
  short_sha: z.string(),
  pushed_at: z.string(),
  author_login: z.string(),
  author_email: z.string(),
  commit_message: z.string(),
  pr_number: z.number().int().nullable(),
  pr_url: z.string().nullable(),
  content_sha256: z.string(),
  content_s3_key: z.string(),
  content_size: z.number().int().nonnegative(),
});
export type PolicyVersion = z.infer<typeof PolicyVersionSchema>;

export const SliceMetricSchema = z.enum([
  'auto_approval',
  'ttm',
  'ttfr',
  'revert_rate',
  'override_rate',
  'council_wall_clock',
]);
export type SliceMetric = z.infer<typeof SliceMetricSchema>;

export const SliceDimensionSchema = z.enum([
  'size_bucket',
  'path_category',
  'change_type',
  'author_type',
  'primary_language',
]);
export type SliceDimension = z.infer<typeof SliceDimensionSchema>;

export const SliceRollupRowSchema = z.object({
  pk: z.string(),
  sk: z.string(),
  org: z.string(),
  repo: z.string(),
  day: z.string(),
  metric: SliceMetricSchema,
  dimension: SliceDimensionSchema,
  bucket_counts: z.record(z.string(), z.number().int().nonnegative()),
  bucket_numerators: z.record(z.string(), z.number().int().nonnegative()),
  ttm_sum_seconds: z.record(z.string(), z.number().int().nonnegative()).optional(),
  ttm_count: z.record(z.string(), z.number().int().nonnegative()).optional(),
  ttfr_sum_seconds: z.record(z.string(), z.number().int().nonnegative()).optional(),
  ttfr_count: z.record(z.string(), z.number().int().nonnegative()).optional(),
  // Per-bucket samples for the council_wall_clock metric. Each value is a
  // duration_ms (council end-to-end wall-clock) for one CI artifact. Stored as
  // an array (not sum+count) because the metric is reported as p50/p95, which
  // cannot be reconstructed from sum+count. Typical storage ~400 bytes / day;
  // worst case ~8KB / day for hot repos. Optional so existing rows parse
  // unchanged.
  council_duration_ms_samples: z.record(z.string(), z.array(z.number().int().nonnegative())).optional(),
  computed_at: z.string().datetime(),
  // Set on SLICE_ROLLUP_ALL#<day> rows only — count of (org, repo) pairs whose
  // per-repo rows were summed into this all-orgs row. Observability only.
  n_repos_contributed: z.number().int().nonnegative().optional(),
});
export type SliceRollupRow = z.infer<typeof SliceRollupRowSchema>;
